import { Globe, Code2, Cloud, Smartphone, BrainCircuit, ShoppingCart, PenTool, Compass, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "../../config/site";

const SERVICES = [
  {
    icon: Globe,
    title: "Website Development",
    desc: "Modern, responsive and conversion-focused business websites, corporate websites, landing pages and custom web experiences.",
    features: ["Business Websites", "Corporate Websites", "Landing Pages", "WordPress", "WooCommerce", "SEO-friendly development"],
    service: "Website Development",
  },
  {
    icon: Code2,
    title: "Custom Software Development",
    desc: "Powerful custom software designed to automate workflows and solve unique business requirements.",
    features: ["Business Software", "ERP", "CRM", "Admin Dashboards", "Workflow Automation", "Custom APIs"],
    service: "Custom Software",
  },
  {
    icon: Cloud,
    title: "SaaS Development",
    desc: "Build scalable SaaS products with subscriptions, dashboards, multi-user access, billing and cloud infrastructure.",
    features: ["Multi-Tenant SaaS", "Subscription Systems", "User Management", "Admin Dashboard", "Payment Integration", "Analytics"],
    service: "SaaS Development",
  },
  {
    icon: Smartphone,
    title: "Mobile App Development",
    desc: "Modern mobile applications for Android and iOS with intuitive interfaces and scalable backend systems.",
    features: ["Android Apps", "iOS Apps", "Flutter", "API Integration", "Push Notifications", "Payment Integration"],
    service: "Mobile App",
  },
  {
    icon: BrainCircuit,
    title: "AI & Automation",
    desc: "Use AI and automation to improve productivity, customer experience and business operations.",
    features: ["AI Applications", "AI Assistants", "Content Automation", "Business Automation", "AI API Integration", "Smart Analytics"],
    service: "AI & Automation",
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce Development",
    desc: "Complete e-commerce solutions that help businesses sell products online and manage their operations.",
    features: ["Online Stores", "Product Management", "Payment Gateway", "Order Management", "WooCommerce", "Custom E-commerce"],
    service: "E-commerce",
  },
  {
    icon: PenTool,
    title: "UI/UX & Product Design",
    desc: "Clean, intuitive and conversion-focused interfaces for websites, SaaS platforms and mobile applications.",
    features: ["Product Design", "Design Systems", "Prototyping"],
    service: "Digital Consultancy",
  },
  {
    icon: Compass,
    title: "Digital Consultancy",
    desc: "Technology consulting to help businesses choose the right digital strategy, tools and architecture.",
    features: ["Technology Strategy", "Architecture", "Tooling"],
    service: "Digital Consultancy",
  },
];

export const Services = () => {
  const enquire = (service) => {
    window.dispatchEvent(new CustomEvent("rcs:prefill-service", { detail: service }));
    scrollToSection("#contact");
  };

  return (
    <section id="services" className="relative bg-white py-24 sm:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <Eyebrow>Our Services</Eyebrow>
            <h2 className="mt-5 font-jakarta text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="services-heading">
              End-to-End Digital Solutions
            </h2>
          </div>
          <p className="max-w-sm font-inter text-sm leading-relaxed text-slate-500">
            One team for strategy, design, engineering and long-term support — across web, software, SaaS, mobile and AI.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 4) * 0.08}>
              <article
                className="group relative flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-[0_28px_50px_-24px_rgba(17,24,39,0.28)]"
                data-testid={`service-card-${i + 1}`}
              >
                <span className="absolute right-5 top-5 font-mono2 text-[11px] font-semibold tracking-widest text-slate-300 transition-colors duration-300 group-hover:text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-mist text-navy ring-1 ring-slate-200 transition-colors duration-300 group-hover:bg-navy group-hover:text-gold-light group-hover:ring-navy">
                  <s.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-jakarta text-[17px] font-bold leading-snug text-navy">{s.title}</h3>
                <p className="mt-2.5 font-inter text-[13px] leading-relaxed text-slate-500">{s.desc}</p>
                <ul className="mt-4 flex flex-wrap gap-1.5">
                  {s.features.map((f) => (
                    <li key={f} className="rounded-full border border-slate-200 bg-mist px-2.5 py-1 font-mono2 text-[10px] text-slate-600">
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => enquire(s.service)}
                  data-testid={`service-enquire-${i + 1}`}
                  className="mt-5 inline-flex items-center gap-1.5 self-start border-t border-slate-100 pt-4 font-jakarta text-[12.5px] font-semibold text-navy transition-colors duration-300 hover:text-gold"
                >
                  Enquire about this
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
